import { useCallback, useState } from 'react'
import { MdMoreVert } from 'react-icons/md'
import {
  useDisclosure,
  AlertStatus,
  Button,
  HStack,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  VStack,
} from '@ritualco/jutsu'
import {
  useDeleteIncentivePlan,
  useGetIncentivePlan,
  usePatchIncentivePlan,
  usePauseIncentivePlan,
  usePublishIncentivePlan,
  useResumeIncentivePlan,
} from 'api/endpoints/incentivePlan'
import {
  useGetUserDefinedMerchantLists,
  useMerchantListQuery,
} from 'api/endpoints/merchantLists'
import { useGetPaymentMethods } from 'api/endpoints/paymentMethod'
import { useGetWorkspaceMembersMapped } from 'api/endpoints/workspaceMembers'
import Loading from 'components/Loading'
import { IncentiveDeleteModal } from 'components/Modals/IncentiveDeleteModal'
import { IncentiveDetailsModalOnSubmit } from 'components/Modals/IncentiveDetailsModal'
import { NoPaymentMethodModal } from 'components/Modals/NoPaymentMethodModal'
import { MaxWidthPage } from 'components/PageTemplates'
import { useMessage } from 'contexts/MessageContext'
import { useContent } from 'lib/content/react'
import {
  IncentivePlanMerchantEligibility,
  IncentivePlanUserTargeting,
  MerchantEligibilityType,
  MerchantListType,
  MerchantRadiusUnit as ApiMerchantRadiusUnit,
  OrganizationType,
  TargetingListType,
  WorkspaceConnectionStatusEnum,
  WorkspaceMemberStatus,
} from 'lib/generated/api'
import { useHistory } from 'lib/router'
import { selectOrganizationType } from 'state/selectors/organization'
import { useStore } from 'state/store'
import {
  IncentiveDetails,
  MerchantParticipation,
  MerchantParticipationType,
  MerchantRadiusUnit,
} from 'types/entities'
import { DetailsSection } from './DetailsSection'
import { ParticipationSection } from './ParticipationSection'
import { RecipientsSection } from './RecipientsSection'
import { AlertBanner, Subheading } from './components'
import { IncentiveRecipientsOnSubmit } from './types'
import { mapToIncentivePlanConfiguration } from './utils'

const merchantEligibilityTypeMap: Record<
  MerchantParticipationType,
  MerchantEligibilityType
> = {
  all_merchants: MerchantEligibilityType.AllMerchants,
  workspace: MerchantEligibilityType.Workspace,
  radius: MerchantEligibilityType.Radius,
  merchant_list: MerchantEligibilityType.MerchantLists,
}

const merchantRadiusUnitMap: Record<
  MerchantRadiusUnit,
  ApiMerchantRadiusUnit
> = {
  kilometer: ApiMerchantRadiusUnit.Kilometer,
  mile: ApiMerchantRadiusUnit.Mile,
}

export type IncentivesProps = {
  organizationId: string
  workspaceId: string
  incentivePlanId: string
  currencyCode: string
  timezone: string
}

export const Incentive = ({
  organizationId,
  workspaceId,
  incentivePlanId,
  currencyCode,
  timezone,
}: IncentivesProps) => {
  const { formatMessage } = useContent()
  const { addMessage } = useMessage()
  const history = useHistory()
  const remove = useDeleteIncentivePlan()
  const patch = usePatchIncentivePlan()
  const pause = usePauseIncentivePlan()
  const publish = usePublishIncentivePlan()
  const resume = useResumeIncentivePlan()
  const organizationType = useStore(selectOrganizationType)
  const [showNoPaymentInfo, setShowNoPaymentInfo] = useState(false)
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure()

  const toast = useCallback(
    (status: AlertStatus, id: string) => {
      addMessage({
        status,
        description: formatMessage({ id }),
        isClosable: false,
      })
    },
    [addMessage, formatMessage]
  )

  const { data, status } = useGetIncentivePlan(
    { organizationId, workspaceId, incentivePlanId },
    timezone
  )

  const merchantListId = data?.merchantParticipation.merchantList
    ? data?.merchantParticipation.merchantList
    : ''
  const { data: merchantListData } = useMerchantListQuery({
    workspaceId,
    organizationId,
    merchantListId,
    excludeMerchants: true,
  })

  const paymentMethods = useGetPaymentMethods({ workspaceId })

  const merchantLists = useGetUserDefinedMerchantLists(
    { organizationId, workspaceId },
    organizationType === OrganizationType.PropertyManager
  )

  const workspaceMembers = useGetWorkspaceMembersMapped({
    organizationId,
    workspaceId,
    limit: 1,
  })

  const requestParams = { organizationId, workspaceId, incentivePlanId }

  const handleLaunch = () => {
    paymentMethods.refetch().then(response => {
      const paymentMethodsExists =
        response.data?.results && response.data.results.length > 0
      if (!paymentMethodsExists) {
        setShowNoPaymentInfo(true)
      } else {
        publish.mutate(requestParams, {
          onSuccess: () =>
            toast('success', 'pmportal.incentives.launchSuccess'),
          onError: () => toast('error', 'pmportal.incentives.launchError'),
        })
      }
    })
  }

  const handleMerchantParticipationSubmission = (
    merchantParticipation: MerchantParticipation
  ) => {
    const {
      original,
      type,
      merchantRadius,
      merchantRadiusUnit,
      merchantList,
    } = merchantParticipation
    const merchantListGroup = original?.merchantListGroup
    if (type === 'merchant_list' && !!merchantListGroup && merchantList) {
      merchantListGroup.merchantLists = [
        {
          merchantListId: merchantList,
          // name is a required field, but is irrelevant and will be ignored
          name: '',
          // type is a required field, but is irrelevant and will be ignored
          type: MerchantListType.UserDefined,
        },
      ]
    }
    const merchantEligibility: IncentivePlanMerchantEligibility = {
      type: merchantEligibilityTypeMap[type],
      merchantRadius: type === 'radius' ? merchantRadius : undefined,
      merchantRadiusUnit:
        type === 'radius' && merchantRadiusUnit
          ? merchantRadiusUnitMap[merchantRadiusUnit]
          : undefined,
    }

    patch.mutate(
      {
        ...requestParams,
        incentivePlanConfiguration: { merchantEligibility },
      },
      {
        onSuccess: () => {
          toast('success', 'pmportal.incentives.updateSuccess')
        },
      }
    )
  }

  const handleIncentiveDetailsSubmit: IncentiveDetailsModalOnSubmit = values => {
    const config = mapToIncentivePlanConfiguration(values as IncentiveDetails)
    patch.mutate(
      {
        ...requestParams,
        incentivePlanConfiguration: config,
      },
      {
        onSuccess: () => toast('success', 'pmportal.incentives.updateSuccess'),
      }
    )
  }

  const handleIncentiveRecipientsSubmit: IncentiveRecipientsOnSubmit = ({
    allCompanies,
    allPeople,
    companies,
    people,
  }) => {
    const userTargeting: IncentivePlanUserTargeting = {}
    if (allCompanies) {
      userTargeting.worspaceGroupListType = TargetingListType.All
    } else if (!!companies) {
      userTargeting.worspaceGroupListType = TargetingListType.Custom
      const workspaceGroupId =
        data?.recipients.original?.workspaceGroup?.id || ''
      userTargeting.workspaceGroup = {
        id: workspaceGroupId,
        name: '',
        connectionLists: [
          {
            id: '',
            name: '',
            connections: companies.map(id => ({
              connectionId: id,
              status: WorkspaceConnectionStatusEnum.Confirmed,
            })),
          },
        ],
      }
    }
    if (allPeople) {
      userTargeting.userGroupListType = TargetingListType.All
    } else if (!!people) {
      userTargeting.userGroupListType = TargetingListType.Custom
      const userGroupId = data?.recipients.original?.userGroup?.id || ''
      userTargeting.userGroup = {
        id: userGroupId,
        name: '',
        memberLists: [
          {
            id: '',
            name: '',
            members: people.map(id => ({
              workspaceMemberId: id,
              createdAt: new Date(0),
              status: WorkspaceMemberStatus.Active,
            })),
          },
        ],
      }
    }
    patch.mutate(
      {
        ...requestParams,
        incentivePlanConfiguration: {
          userTargeting,
        },
      },
      {
        onSuccess: () => toast('success', 'pmportal.incentives.updateSuccess'),
      }
    )
  }

  if (status === 'loading' || merchantLists.isLoading) {
    return <Loading />
  }

  if (status === 'error') {
    // TODO: handle error state
    return <span>Something went wrong!</span>
  }

  return (
    <MaxWidthPage
      alert={
        <AlertBanner
          organizationType={organizationType}
          isMissingPeople={!workspaceMembers.data?.results.length}
          hasChangesDrafted={data?.hasChangesDrafted || false}
          isDraft={data?.status === 'draft'}
        />
      }
      heading={
        data?.incentiveDetails.name ||
        formatMessage({ id: 'pmportal.incentives.untitledIncentive' })
      }
      actions={
        <HStack spacing={2}>
          {data?.canPause && (
            <Button
              variant="outline"
              onClick={() => {
                pause.mutate(requestParams, {
                  onSuccess: () =>
                    toast('success', 'pmportal.incentives.pauseSuccess'),
                  onError: () =>
                    toast('error', 'pmportal.incentives.genericTryAgain'),
                })
              }}
            >
              {formatMessage({ id: 'pmportal.incentives.pauseAction' })}
            </Button>
          )}
          {data?.canResume && (
            <Button
              colorScheme="yellow"
              variant="outline"
              onClick={() => {
                resume.mutate(requestParams, {
                  onSuccess: () =>
                    toast('success', 'pmportal.incentives.resumeSuccess'),
                  onError: () =>
                    toast('error', 'pmportal.incentives.genericTryAgain'),
                })
              }}
            >
              {formatMessage({ id: 'pmportal.incentives.resumeAction' })}
            </Button>
          )}
          {data?.status === 'draft' && (
            <Button
              disabled={!data?.canLaunch}
              isLoading={paymentMethods.isLoading}
              onClick={handleLaunch}
            >
              {formatMessage({ id: 'pmportal.incentives.launch' })}
            </Button>
          )}
          {data?.canRelaunch && (
            <Button
              onClick={() => {
                publish.mutate(requestParams, {
                  onSuccess: () =>
                    toast('success', 'pmportal.incentives.relaunchSuccess'),
                  onError: () =>
                    toast('error', 'pmportal.incentives.relaunchError'),
                })
              }}
            >
              {formatMessage({ id: 'pmportal.incentives.relaunch' })}
            </Button>
          )}
          <Menu>
            <MenuButton
              as={IconButton}
              colorScheme="transparent"
              variant="ghost"
              icon={<Icon as={MdMoreVert} />}
            />
            <MenuList zIndex="dropdown">
              {data?.canLaunch && (
                <MenuItem onClick={handleLaunch}>
                  {formatMessage({ id: 'pmportal.incentives.launchAction' })}
                </MenuItem>
              )}
              {data?.canRelaunch && (
                <MenuItem
                  onClick={() => {
                    publish.mutate(requestParams, {
                      onSuccess: () =>
                        toast('success', 'pmportal.incentives.relaunchSuccess'),
                      onError: () =>
                        toast('error', 'pmportal.incentives.relaunchError'),
                    })
                  }}
                >
                  {formatMessage({
                    id: 'pmportal.incentives.relaunchAction',
                  })}
                </MenuItem>
              )}
              {data?.canPause && (
                <MenuItem
                  onClick={() => {
                    pause.mutate(requestParams, {
                      onSuccess: () =>
                        toast('success', 'pmportal.incentives.pauseSuccess'),
                      onError: () =>
                        toast('error', 'pmportal.incentives.genericTryAgain'),
                    })
                  }}
                >
                  {formatMessage({ id: 'pmportal.incentives.pauseAction' })}
                </MenuItem>
              )}
              {data?.canResume && (
                <MenuItem
                  onClick={() => {
                    resume.mutate(requestParams, {
                      onSuccess: () =>
                        toast('success', 'pmportal.incentives.resumeSuccess'),
                      onError: () =>
                        toast('error', 'pmportal.incentives.genericTryAgain'),
                    })
                  }}
                >
                  {formatMessage({ id: 'pmportal.incentives.resumeAction' })}
                </MenuItem>
              )}
              {data?.canDelete && (
                <MenuItem onClick={onDeleteOpen}>
                  {formatMessage({ id: 'pmportal.incentives.deleteAction' })}
                </MenuItem>
              )}
              <MenuItem
                onClick={() => {
                  // TODO(WT-1888): handle copying
                }}
              >
                {formatMessage({ id: 'pmportal.incentives.copyAction' })}
              </MenuItem>
            </MenuList>
          </Menu>
        </HStack>
      }
      subheading={<Subheading status={data?.status} />}
    >
      <VStack spacing={12} alignItems="stretch">
        <DetailsSection
          incentiveDetails={data?.incentiveDetails}
          currencyCode={currencyCode}
          timezone={timezone}
          onSubmit={handleIncentiveDetailsSubmit}
        />
        <RecipientsSection
          recipients={data?.recipients}
          organizationId={organizationId}
          workspaceId={workspaceId}
          onSubmit={handleIncentiveRecipientsSubmit}
        />
        <ParticipationSection
          merchantParticipation={data?.merchantParticipation}
          merchantListName={merchantListData?.name}
          merchantLists={merchantLists?.data}
          handleFormSubmission={handleMerchantParticipationSubmission}
        />
      </VStack>
      <IncentiveDeleteModal
        incentivePlanId={incentivePlanId}
        isLoading={remove.isLoading}
        isOpen={isDeleteOpen}
        onClose={onDeleteClose}
        onConfirm={incentivePlanId => {
          remove.mutate(
            {
              organizationId,
              workspaceId,
              incentivePlanId,
            },
            {
              onSuccess: () => {
                onDeleteClose()
                toast('success', 'pmportal.incentives.deleteSuccess')
              },
              onError: () =>
                toast('error', 'pmportal.incentives.genericTryAgain'),
            }
          )
        }}
      />
      <NoPaymentMethodModal
        isOpen={showNoPaymentInfo}
        onCancel={() => setShowNoPaymentInfo(false)}
        onConfirm={() =>
          history.push(`/workspaces/${workspaceId}/settings/billing`)
        }
      />
    </MaxWidthPage>
  )
}
